import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { safeFetch } from "../lib/safeFetch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Minimum time the launch loader stays visible before the game iframe is shown.
// Per operator request the branded loader is now a short 2-second flash
// (matching the look of the aggregator's own s.dstgaming.app loader).
const MIN_LOADER_MS = 2000;

// If the iframe doesn't fire `load` within this window after we reveal it, we
// assume the provider blocked the embed (Evolution / Pragmatic Live etc are
// known to gate by domain whitelist) and show a friendly fallback panel.
const IFRAME_LOAD_TIMEOUT_MS = 9000;

/**
 * Provider → themed accent palette. The brand mark, light rays and progress
 * bar all subtly shift to match the live game's provider so the loading screen
 * feels like part of the game itself.
 *
 * Match is case-insensitive against the game's `provider` field. Falls back to
 * the default emerald/gold Maxlive88 theme.
 */
const PROVIDER_THEMES = {
  default: {
    name: "Maxlive88",
    accent: "#19a55c", accent2: "#2bd07c", gold: "#ffc933",
    bgInner: "#0e3a25", bgMid: "#062014", bgOuter: "#02060a",
    rayA: "rgba(255,201,51,0.30)", rayB: "rgba(25,165,92,0.35)",
  },
  pragmatic: {
    name: "Pragmatic Play",
    accent: "#3b82f6", accent2: "#60a5fa", gold: "#22d3ee",
    bgInner: "#0b2655", bgMid: "#071737", bgOuter: "#02060a",
    rayA: "rgba(59,130,246,0.32)", rayB: "rgba(34,211,238,0.32)",
  },
  jili: {
    name: "JILI",
    accent: "#ef4444", accent2: "#fb7185", gold: "#fbbf24",
    bgInner: "#4a1219", bgMid: "#280912", bgOuter: "#0a0306",
    rayA: "rgba(239,68,68,0.34)", rayB: "rgba(251,191,36,0.30)",
  },
  pgsoft: {
    name: "PG Soft",
    accent: "#a855f7", accent2: "#c084fc", gold: "#f0abfc",
    bgInner: "#3b0a5e", bgMid: "#1f0432", bgOuter: "#08020e",
    rayA: "rgba(168,85,247,0.36)", rayB: "rgba(240,171,252,0.30)",
  },
  evolution: {
    name: "Evolution",
    accent: "#f97316", accent2: "#fb923c", gold: "#facc15",
    bgInner: "#3a1505", bgMid: "#1f0a02", bgOuter: "#080300",
    rayA: "rgba(249,115,22,0.34)", rayB: "rgba(250,204,21,0.30)",
  },
  spribe: {
    name: "Spribe",
    accent: "#10b981", accent2: "#34d399", gold: "#fde047",
    bgInner: "#053e2c", bgMid: "#021f17", bgOuter: "#000806",
    rayA: "rgba(16,185,129,0.34)", rayB: "rgba(253,224,71,0.30)",
  },
  habanero: {
    name: "Habanero",
    accent: "#dc2626", accent2: "#f87171", gold: "#fbbf24",
    bgInner: "#3f0c0c", bgMid: "#220606", bgOuter: "#080202",
    rayA: "rgba(220,38,38,0.34)", rayB: "rgba(251,191,36,0.30)",
  },
  microgaming: {
    name: "Microgaming",
    accent: "#0891b2", accent2: "#22d3ee", gold: "#fde047",
    bgInner: "#053246", bgMid: "#021823", bgOuter: "#000509",
    rayA: "rgba(8,145,178,0.34)", rayB: "rgba(253,224,71,0.30)",
  },
};

function resolveProviderTheme(providerName) {
  const key = (providerName || "").toLowerCase().replace(/[^a-z]/g, "");
  if (PROVIDER_THEMES[key]) return PROVIDER_THEMES[key];
  // Fuzzy fallbacks for compound names ("PG Soft" → "pgsoft", "Pragmatic Play" → "pragmatic")
  if (key.startsWith("pragmatic")) return PROVIDER_THEMES.pragmatic;
  if (key.startsWith("pg")) return PROVIDER_THEMES.pgsoft;
  if (key.startsWith("evo")) return PROVIDER_THEMES.evolution;
  if (key.startsWith("hab")) return PROVIDER_THEMES.habanero;
  if (key.startsWith("micro")) return PROVIDER_THEMES.microgaming;
  if (key.startsWith("spribe")) return PROVIDER_THEMES.spribe;
  if (key.includes("jili")) return PROVIDER_THEMES.jili;
  return PROVIDER_THEMES.default;
}

/**
 * useGameLaunch — opens a game via the iGaming aggregator in fullscreen,
 * with a branded 3-4s loading screen first so players never see a black flash.
 */
export function useGameLaunch() {
  const { user, applyWcktLaunchOptimistic, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [launchState, setLaunchState] = useState(null);
  // null
  // | { phase: "loading", title, startedAt }
  // | { phase: "ready",   title, startedAt, url, demo, session_token, message }

  const launchGame = useCallback(async (gameId, gameTitle, gameProvider) => {
    if (!gameId) return;
    if (!user) {
      toast.message("Please log in to play");
      navigate("/login");
      return;
    }

    // ── Pre-open a blank TAB SYNCHRONOUSLY for transfer-wallet providers ──
    // Browsers only allow window.open() during a direct user gesture (this
    // click). After our `await fetch(...)` below, the gesture is consumed and
    // any window.open() call gets blocked. So when the game looks like a
    // transfer-wallet provider (currently only WICKET / WCKT), we open an
    // empty tab RIGHT NOW (still inside the user-gesture stack) and just
    // navigate it once the launch URL arrives.
    //
    // Note: we deliberately keep the placeholder MINIMAL (just a small
    // "Loading WICKET…" line) so the new tab feels native — opening directly
    // into the game, not into a heavy branded interstitial. Per operator
    // direction: WICKET should open straight in a new browser tab without
    // popup-window styling or full-screen branding.
    const isLikelyTransferWallet = /^dst_WCKT_/i.test(gameId) ||
      /wicket/i.test(gameProvider || "") ||
      /wckt/i.test(gameProvider || "");
    let prewarmedPopup = null;
    if (isLikelyTransferWallet) {
      try {
        // No `noopener` here — we need a real window reference to navigate it
        // once the launch URL arrives. The destination is our own DST portal
        // flow, so the opener relationship is harmless.
        prewarmedPopup = window.open("about:blank", "_blank");
        // Minimal placeholder — a single centred line so the tab doesn't
        // flash a white about:blank for the 1-3s that DST takes to return
        // the launch URL. Professional cricket-themed splash works
        // identically on phones and desktops (clamp() font sizes + svh).
        if (prewarmedPopup && prewarmedPopup.document) {
          try {
            prewarmedPopup.document.open();
            prewarmedPopup.document.write(`<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
<meta name="theme-color" content="#062014" />
<title>9 Wickets — Loading</title>
<style>
*,*::before,*::after{box-sizing:border-box}
html,body{margin:0;padding:0;height:100%;background:#02060a;color:#e6f5ec;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Inter,Roboto,sans-serif;overflow:hidden;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}
body{
  min-height:100vh;min-height:100svh;
  display:flex;align-items:center;justify-content:center;
  background:radial-gradient(ellipse at 50% 32%,#0e3a25 0%,#062014 55%,#02060a 100%);
  position:relative;overflow:hidden;
}
/* Stadium lights (decorative) */
body::before{
  content:"";position:absolute;inset:0;pointer-events:none;
  background:
    radial-gradient(circle at 20% 0%, rgba(255,201,51,0.15) 0%, transparent 32%),
    radial-gradient(circle at 80% 0%, rgba(43,208,124,0.18) 0%, transparent 32%),
    radial-gradient(circle at 50% 100%, rgba(25,165,92,0.12) 0%, transparent 45%);
  animation:rays 4.2s ease-in-out infinite;
}
@keyframes rays{0%,100%{opacity:.55}50%{opacity:.95}}
/* Pitch grid */
body::after{
  content:"";position:absolute;inset:0;pointer-events:none;opacity:.07;
  background-image:linear-gradient(to right,rgba(255,255,255,.7) 1px,transparent 1px);
  background-size:44px 100%;
  -webkit-mask-image:radial-gradient(ellipse at 50% 50%,#000 30%,transparent 80%);
          mask-image:radial-gradient(ellipse at 50% 50%,#000 30%,transparent 80%);
}
.wrap{position:relative;z-index:2;width:100%;max-width:560px;padding:32px 24px;text-align:center}
.brand{display:inline-flex;align-items:baseline;gap:.6rem;animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.05s}
.brand .nine{
  font-size:clamp(40px,8vw,64px);line-height:1;font-weight:900;letter-spacing:-.02em;
  background:linear-gradient(180deg,#ffc933 0%,#fff5d4 55%,#ffc933 100%);
  -webkit-background-clip:text;background-clip:text;color:transparent;
  filter:drop-shadow(0 4px 18px rgba(255,201,51,.35));
}
.brand .word{
  font-size:clamp(24px,5vw,40px);line-height:1;font-weight:800;letter-spacing:.01em;color:#fff;
}
.tag{
  margin-top:.4rem;font-size:clamp(10px,2.2vw,12px);letter-spacing:.22em;text-transform:uppercase;
  color:#2bd07c;font-weight:600;animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.18s
}
.pitch{
  position:relative;margin:clamp(22px,4vw,36px) auto 0;height:clamp(80px,14vw,108px);
  max-width:340px;animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.32s
}
.ground{
  position:absolute;left:0;right:0;bottom:14px;height:2px;border-radius:9999px;
  background:linear-gradient(90deg,transparent 0%,rgba(25,165,92,.55) 18%,#2bd07c 50%,rgba(25,165,92,.55) 82%,transparent 100%);
  box-shadow:0 0 14px rgba(43,208,124,.45);
}
.stumps{position:absolute;right:18px;bottom:16px;display:flex;align-items:flex-end;gap:6px}
.stumps span{
  display:block;width:5px;height:44px;border-radius:2px 2px 1px 1px;
  background:linear-gradient(180deg,#fff 0%,#ffc933 100%);
  box-shadow:0 0 6px rgba(255,201,51,.65);
}
.bails{position:absolute;right:18px;bottom:58px;width:33px;height:2px;border-radius:9999px;background:linear-gradient(90deg,#fff,#ffc933)}
.ball{
  position:absolute;bottom:16px;left:6%;width:14px;height:14px;border-radius:50%;
  background:radial-gradient(circle at 30% 30%,#ff5252 0%,#b91c1c 65%,#5a0a0a 100%);
  box-shadow:0 0 10px rgba(255,80,80,.6),inset -2px -2px 3px rgba(0,0,0,.5);
  animation:bounce 2.4s cubic-bezier(.36,0,.66,-.56) infinite;
}
@keyframes bounce{
  0%{left:6%;bottom:16px;transform:rotate(0)}
  20%{left:26%;bottom:50px;transform:rotate(180deg)}
  40%{left:46%;bottom:16px;transform:rotate(360deg)}
  55%{left:60%;bottom:38px;transform:rotate(520deg)}
  70%{left:72%;bottom:16px;transform:rotate(680deg)}
  85%{left:80%;bottom:28px;transform:rotate(820deg)}
  100%{left:84%;bottom:16px;transform:rotate(960deg)}
}
.status{
  margin-top:clamp(18px,3.4vw,28px);
  font-size:clamp(12px,2.4vw,14px);font-weight:600;letter-spacing:.04em;color:rgba(255,255,255,.78);
  animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.48s
}
.bar{
  position:relative;margin:14px auto 0;height:3px;width:100%;max-width:260px;border-radius:9999px;
  background:rgba(255,255,255,.10);overflow:hidden;
  animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.6s
}
.bar i{
  position:absolute;inset:0 auto 0 0;width:33%;border-radius:9999px;
  background:linear-gradient(90deg,transparent,#ffc933,#2bd07c,transparent);
  box-shadow:0 0 10px rgba(255,201,51,.65);
  animation:sweep 1.6s cubic-bezier(.6,0,.4,1) infinite;
}
@keyframes sweep{0%{transform:translateX(-110%)}100%{transform:translateX(310%)}}
.trust{
  margin-top:clamp(20px,3.6vw,32px);
  display:flex;align-items:center;justify-content:center;flex-wrap:wrap;gap:10px 14px;
  font-size:clamp(10px,2vw,11px);letter-spacing:.16em;text-transform:uppercase;color:rgba(255,255,255,.42);
  animation:rise .55s cubic-bezier(.21,.96,.36,1) both;animation-delay:.74s
}
.trust b{font-weight:600;color:rgba(255,255,255,.55);display:inline-flex;align-items:center;gap:6px}
.trust b svg{width:11px;height:11px}
.dot{opacity:.35}
@keyframes rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@media (prefers-reduced-motion:reduce){
  .ball,.bar i,body::before{animation:none!important}
  .brand,.tag,.pitch,.status,.bar,.trust{animation:none!important;opacity:1!important;transform:none!important}
}
</style></head>
<body>
  <div class="wrap" role="status" aria-label="Loading 9 Wickets">
    <div class="brand"><span class="nine">9</span><span class="word">Wickets</span></div>
    <div class="tag">Live Cricket · Exchange · Sportsbook</div>
    <div class="pitch" aria-hidden="true">
      <div class="ground"></div>
      <div class="bails"></div>
      <div class="stumps"><span></span><span></span><span></span></div>
      <div class="ball"></div>
    </div>
    <div class="status">Opening secure session…</div>
    <div class="bar" aria-hidden="true"><i></i></div>
    <div class="trust">
      <b><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"/></svg>Secure</b>
      <span class="dot">·</span><b>Licensed</b>
      <span class="dot">·</span><b>Fair Play</b>
    </div>
  </div>
</body></html>`);
            prewarmedPopup.document.close();
          } catch { /* document write blocked — show empty tab instead */ }
        }
      } catch { /* popup blocker — handled below */ }
    }

    const startedAt = Date.now();
    // For WICKET we DO NOT show the fullscreen loader modal on the originating
    // tab. The new tab is opening at the same time, and a heavy modal on top
    // of the player's home page makes them think the launch is happening
    // ON our site (when actually it's now in a separate tab). Per operator
    // request: WICKET launches feel like a "direct new tab open".
    if (!isLikelyTransferWallet) {
      setLaunchState({
        phase: "loading",
        title: gameTitle || gameId,
        provider: gameProvider || "",
        startedAt,
        username: user?.username || "",
        client_ip: "",
      });
    }
    try {
      const token = localStorage.getItem("access_token");
      // safeFetch hard-caps the launch request at 15s — without it, a hanging
      // DST API would leave the player stuck on the loader screen indefinitely
      // (especially noticeable on mobile where they can't easily tell it's
      // stuck vs. genuinely loading). After 15s safeFetch aborts and the
      // catch below shows a clear error.
      const res = await safeFetch(`${BACKEND_URL}/api/dst/launch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ game_id: gameId }),
        timeoutMs: 15000,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        // User-facing message strategy:
        //   • If backend included a friendly Bangla `detail` (HTTP 4xx with
        //     a specific reason — e.g. exposure-blocked, refund-lock active,
        //     credit-limit hit) we surface THAT exact text. The backend
        //     curates these messages, so they are safe to display.
        //   • Otherwise fall back to a generic "Refresh first" Bangla nudge
        //     for WCKT, or "try another game" for everything else.
        const backendMsg = (data?.detail || data?.message || "").toString().trim();
        const looksFriendly =
          backendMsg.length > 0 &&
          backendMsg.length < 280 &&
          // suppress raw upstream-provider error codes / stack-trace-looking text
          !/^\{|^\[|traceback|error:|exception|<html/i.test(backendMsg);
        if (looksFriendly) {
          toast.error(backendMsg, { duration: 7000 });
        } else if (isLikelyTransferWallet) {
          toast.error("⚠️ 9 Wicket-এ প্রবেশ করার জন্য প্রতিবার গেমে ঢোকার আগে Balance Refresh করা অবশ্যই বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।");
        } else {
          toast.error("This game is temporarily unavailable. Please try a different one.");
        }
        // Close the pre-warmed popup if launch failed
        try { if (prewarmedPopup && !prewarmedPopup.closed) prewarmedPopup.close(); } catch { /* ignore */ }
        setLaunchState(null);
        return;
      }

      // ── WICKET (WCKT) & other transfer-wallet providers open in a NEW TAB ──
      // The DST launch URL for WICKET is a SINGLE-USE token. Loading it inside
      // an iframe causes the modal's pre-render + the iframe load to consume
      // the token twice, which is why the player saw `{"status":"1001"}`
      // (token expired). Opening in a fresh new tab guarantees the URL is
      // hit exactly once, so the WICKET portal opens cleanly.
      if (data.open_in_new_tab && data.url) {
        // ── MOBILE "DOUBLE BALANCE" FIX (2026-06-28) ─────────────────────
        // On mobile, the original site tab gets suspended/killed the moment
        // the WCKT game tab opens, so the backend's WebSocket balance-push
        // (site balance → 0, WCKT pill → X) often never reaches the UI.
        // Optimistically apply it on the foreground tab RIGHT NOW so the
        // player never sees stale "double balance" when they tab back to
        // the site. Backend stays the source of truth — next /auth/me on
        // mount or WS reconnect will reconcile.
        if (typeof applyWcktLaunchOptimistic === "function" && data.deposited_amount > 0) {
          applyWcktLaunchOptimistic(data.deposited_amount);
        }
        // Belt-and-suspenders: also fire /auth/me silently so the canonical
        // post-debit balance lands within ~300 ms. Won't hurt desktop.
        try { if (typeof refreshUser === "function") refreshUser({ silent: true }); } catch { /* ignore */ }

        // Mark this player as having an OPEN WCKT session so the AuthContext
        // visibility-change listener can auto-cashout (transfer the leftover
        // WCKT provider-wallet balance back to the site wallet) the moment
        // the user returns to our tab.
        try {
          sessionStorage.setItem("wckt_active_session", JSON.stringify({
            ts: Date.now(),
            provider_code: data.provider_code || "WCKT",
            session_token: data.session_token || "",
          }));
        } catch { /* storage disabled — cashout will still work via manual call */ }

        // Prefer navigating the pre-warmed popup we opened on click. This is
        // the ONLY reliable way to bypass modern popup blockers when the URL
        // comes from an async fetch. If the prewarm wasn't done (non-WCKT
        // launch that turned out to be new-tab) or it was blocked, fall back
        // to a regular window.open — which may now be blocked.
        let opened = false;
        let openedWindow = null;
        if (prewarmedPopup && !prewarmedPopup.closed) {
          try {
            prewarmedPopup.location.href = data.url;
            opened = true;
            openedWindow = prewarmedPopup;
          } catch { /* cross-origin nav refusal — fall through */ }
        }
        if (!opened) {
          const win = window.open(data.url, "_blank");
          opened = !!win;
          openedWindow = win;
        }
        // Store popup reference globally so AuthContext can check `.closed`
        // before auto-cashing-out (avoids cashing out when the user briefly
        // tabs back to our site while still playing in the WICKET tab).
        try { window.__wcktPopup = openedWindow; } catch { /* ignore */ }
        if (!opened) {
          // Don't use toast.error here — the modal panel below now shows a
          // clear "Open in new tab" button with instructions. A toast on top
          // would just add noise (and players sometimes dismiss it before
          // reading). Inline modal action is the better UX.
          // Surface the URL inside the modal so the player can click it manually.
          setLaunchState({
            phase: "ready",
            startedAt,
            url: data.url,
            title: gameTitle || gameId,
            provider: gameProvider || data.provider || "",
            demo: false,
            provider_code: data.provider_code || "",
            session_token: data.session_token,
            username: data.username || user?.username || "",
            client_ip: data.client_ip || "",
            popup_blocked: true,
          });
          return;
        }
        setLaunchState(null);
        return;
      }

      // Game turned out to NOT be a new-tab launch — close any pre-warmed popup
      // so the player doesn't see an empty tab.
      try { if (prewarmedPopup && !prewarmedPopup.closed) prewarmedPopup.close(); } catch { /* ignore */ }

      setLaunchState({
        phase: "ready",
        startedAt,
        url: data.url,
        title: gameTitle || gameId,
        provider: gameProvider || data.provider || "",
        demo: !!data.demo,
        demo_reason: data.demo_reason || "",
        provider_code: data.provider_code || "",
        game_code: data.game_code || "",
        dst_code: data.dst_code || "",
        session_token: data.session_token,
        message: data.message || "",
        provider_error: data.provider_error || null,
        client_ip: data.client_ip || "",
        username: data.username || user?.username || "",
      });

      // Fallback — if backend couldn't resolve an IPv4 (e.g. user is on a
      // pure-IPv6 network and the ingress only forwarded the v6 address),
      // ask api.ipify.org which always returns the public IPv4. We swap the
      // value in once it arrives so the loader header shows a real IPv4.
      if (!data.client_ip) {
        try {
          const r = await fetch("https://api.ipify.org?format=json", { cache: "no-store" });
          if (r.ok) {
            const j = await r.json();
            const ipv4 = (j?.ip || "").trim();
            if (/^\d{1,3}(\.\d{1,3}){3}$/.test(ipv4)) {
              setLaunchState((cur) => (cur ? { ...cur, client_ip: ipv4 } : cur));
            }
          }
        } catch (_) { /* ignore — header just stays without IP */ }
      }
    } catch (err) {
      // safeFetch aborts → err.name === "AbortError" (timeout). Network drop
      // → TypeError "Failed to fetch". Either way the player is stuck on the
      // loader, so surface a clear, action-oriented message and close any
      // pre-warmed popup so they don't see an empty tab dangling.
      // Branding rule: never mention the upstream aggregator or any internal
      // infrastructure — message must read like it came from Maxlive88 alone.
      const isAbort = err?.name === "AbortError";
      let friendly;
      if (isLikelyTransferWallet) {
        // 9 Wickets-specific Bangla prompt — pending provider-wallet balance
        // is the #1 cause of WCKT launch failures (timeout or network).
        friendly = isAbort
          ? "⏳ 9 Wicket গেম শুরু হতে দেরি হচ্ছে। অনুগ্রহ করে আগে Balance Refresh করুন — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।"
          : "⚠️ 9 Wicket-এ প্রবেশ করার জন্য প্রতিবার গেমে ঢোকার আগে Balance Refresh করা অবশ্যই বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।";
      } else {
        friendly = isAbort
          ? "This game is taking too long to load. Please try a different one."
          : "This game couldn't open right now. Please try a different one.";
      }
      toast.error(friendly);
      try { if (prewarmedPopup && !prewarmedPopup.closed) prewarmedPopup.close(); } catch { /* ignore */ }
      setLaunchState(null);
    }
  }, [user, navigate]);

  const closeModal = () => setLaunchState(null);

  const modal = <GameLaunchModal state={launchState} onClose={closeModal} />;

  return { launchGame, modal };
}

/* ---------------- Fullscreen launcher ---------------- */

function GameLaunchModal({ state, onClose }) {
  const [showGame, setShowGame] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);

  // 📣 Notify the rest of the app (e.g. SiteChatGate) that a game modal is
  // currently open. Sends `{ open: true }` on mount and `{ open: false }` on
  // unmount. The chat FAB listens for this event to hide itself while the
  // user is playing a game.
  useEffect(() => {
    if (!state) return;
    window.dispatchEvent(new CustomEvent("site:game-modal", { detail: { open: true } }));
    return () => {
      window.dispatchEvent(new CustomEvent("site:game-modal", { detail: { open: false } }));
    };
  }, [state]);
  // App-level fullscreen — hides the top header strip so the game iframe
  // covers 100% of the viewport. Also enters browser-native fullscreen via
  // the Fullscreen API when supported (so the OS chrome / tabs also hide on
  // desktop). Both togglable via the maximize button in the header AND via
  // ESC key (fullscreenchange listener keeps state in sync).
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const startedRef = useRef(null);
  const iframeTimerRef = useRef(null);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const isCurrentlyNative =
      !!(document.fullscreenElement || document.webkitFullscreenElement);
    if (!isCurrentlyNative) {
      // Enter — request browser fullscreen on our wrapper (NOT the iframe),
      // so the game's own tap-to-fullscreen behaviour is unaffected and we
      // never get the "screen shake" reported earlier with iframe fullscreen.
      const req =
        el?.requestFullscreen ||
        el?.webkitRequestFullscreen ||
        el?.mozRequestFullScreen ||
        el?.msRequestFullscreen;
      if (req) {
        try {
          const p = req.call(el);
          if (p && typeof p.then === "function") p.catch(() => {});
        } catch { /* fallback: just hide header */ }
      }
      setIsFullscreen(true);
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (exit) {
        try {
          const p = exit.call(document);
          if (p && typeof p.then === "function") p.catch(() => {});
        } catch { /* ignore */ }
      }
      setIsFullscreen(false);
    }
  }, []);

  // Keep `isFullscreen` in sync with the browser (ESC key, F11 etc).
  useEffect(() => {
    const onChange = () => {
      const active =
        !!(document.fullscreenElement || document.webkitFullscreenElement ||
           document.mozFullScreenElement || document.msFullscreenElement);
      setIsFullscreen(active);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // When the launch modal closes, make sure we leave browser-fullscreen too.
  useEffect(() => {
    if (state) return;
    if (document.fullscreenElement || document.webkitFullscreenElement) {
      try { (document.exitFullscreen || document.webkitExitFullscreen).call(document); } catch { /* ignore */ }
    }
    setIsFullscreen(false);
  }, [state]);

  const theme = resolveProviderTheme(state?.provider || "");

  // Lock body + html scroll while open. We also pin the document position so
  // mobile Safari / Chrome don't show/hide their address bar in response to
  // taps inside the iframe — that show/hide was the root cause of the
  // "screen shakes when I click the game" report from operators.
  useEffect(() => {
    if (!state) return;
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
      bodyTop: body.style.top,
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
      scrollY: window.scrollY || window.pageYOffset || 0,
    };
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.height = "100%";
    body.style.top = `-${prev.scrollY}px`;
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      body.style.top = prev.bodyTop;
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      // Restore scroll position
      window.scrollTo(0, prev.scrollY);
    };
  }, [state]);

  // NOTE: we used to auto-exit browser fullscreen here whenever the embedded
  // game tried to enter it (Pragmatic Live / Spribe etc call
  // requestFullscreen on tap). That created a visible "screen shake" — every
  // tap inside the game triggered fullscreenchange → exitFullscreen →
  // fullscreenchange, which resized the viewport on each tap. Now we simply
  // don't allow the iframe to request browser-level fullscreen (see the
  // iframe `allow=` attribute below). Our modal already covers the entire
  // viewport, so the game has no visual gain from real fullscreen, and the
  // X close button always stays reachable.

  // Reset showGame whenever modal closes / opens for a new launch
  useEffect(() => {
    if (!state) {
      setShowGame(false);
      setIframeLoaded(false);
      setIframeFailed(false);
      startedRef.current = null;
      if (iframeTimerRef.current) clearTimeout(iframeTimerRef.current);
      return;
    }
    if (startedRef.current !== state.startedAt) {
      startedRef.current = state.startedAt;
      setShowGame(false);
      setIframeLoaded(false);
      setIframeFailed(false);
    }
  }, [state]);

  // When URL is ready, wait for the minimum loader duration to elapse,
  // then reveal the iframe.
  useEffect(() => {
    if (!state || state.phase !== "ready") return;
    const elapsed = Date.now() - (state.startedAt || Date.now());
    const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
    const t = setTimeout(() => setShowGame(true), remaining);
    return () => clearTimeout(t);
  }, [state]);

  // Once iframe is meant to be visible, watch for failure (no `load` event
  // within IFRAME_LOAD_TIMEOUT_MS → likely blocked by provider domain whitelist
  // or X-Frame-Options).
  useEffect(() => {
    if (!showGame || !state?.url || state?.demo) return;
    if (iframeLoaded) return;
    iframeTimerRef.current = setTimeout(() => {
      setIframeFailed((cur) => (iframeLoaded ? cur : true));
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => { if (iframeTimerRef.current) clearTimeout(iframeTimerRef.current); };
  }, [showGame, state, iframeLoaded]);

  if (!state) return null;

  const showLoader = !showGame;
  // Popup-blocked path: skip the iframe entirely (WICKET tokens are single-use,
  // so attempting iframe + new-tab would burn the token) and show the friendly
  // "Open in new tab" panel immediately. This shaves 9 seconds off the
  // popup-blocked user journey vs. waiting for IFRAME_LOAD_TIMEOUT_MS.
  const showFailure = (showGame && iframeFailed && !iframeLoaded) || !!state.popup_blocked;

  // Header height (in px) — used to push the iframe / loader body below it
  // so the persistent username + IP + game-name + close strip is always
  // visible, exactly like the s.dstgaming.app aggregator page.
  const HEADER_H = 56;

  return (
    <div
      ref={containerRef}
      className="fixed left-0 top-0 right-0 z-[100] bg-white overflow-hidden flex flex-col"
      style={{
        // Use 100svh (small viewport height) so the modal sticks to the
        // *smallest* possible viewport — i.e. the size WITH the browser
        // address bar showing. This way taps inside the iframe never cause
        // the browser to grow/shrink the viewport (which felt like a "screen
        // shake" to the player). Fallback to 100vh for browsers that don't
        // support svh yet.
        height: "100vh",
        // eslint-disable-next-line no-dupe-keys
        minHeight: "100svh",
        // eslint-disable-next-line no-dupe-keys
        maxHeight: "100svh",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
      data-testid="game-launch-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Persistent header — visible during the 2-second branded loader
          AND while the game iframe is running underneath. Mirrors the
          aggregator's own top bar (USER + IP | Game name | X).
          HIDDEN entirely when the player toggles fullscreen mode. */}
      {!isFullscreen && (
        <GameLaunchHeader
          title={state.title}
          username={state.username || ""}
          clientIp={state.client_ip || ""}
          onClose={onClose}
          onToggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          height={HEADER_H}
        />
      )}

      {/* Floating fullscreen-exit button — only visible WHILE fullscreen is
          active. Sits in the top-right corner, tiny, semi-transparent so it
          doesn't get in the way of the game. Tapping it exits fullscreen
          and brings the header strip back. */}
      {isFullscreen && (
        <button
          type="button"
          onClick={toggleFullscreen}
          onTouchEnd={(e) => { e.preventDefault(); toggleFullscreen(); }}
          data-testid="game-launch-fullscreen-exit"
          aria-label="exit fullscreen"
          className="fixed top-3 right-3 z-[125] inline-flex h-9 w-9 items-center justify-center rounded-md bg-black/55 backdrop-blur-sm text-white/90 hover:bg-black/75 active:scale-95 transition-all"
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        >
          {/* Compress / exit-fullscreen icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 14h6v6" />
            <path d="M20 10h-6V4" />
            <path d="M14 10l7-7" />
            <path d="M3 21l7-7" />
          </svg>
        </button>
      )}

      {/* Body — fills the rest of the viewport below the header */}
      <div className="relative flex-1 bg-black">
        {/* Branded loading skin (3 bouncing dots) */}
        {showLoader && (
          <GameLoadingSkin />
        )}

        {/* Game iframe — fills the body, never the header. */}
        {state.phase === "ready" && state.url && !state.demo && (
          <iframe
            key={state.session_token || state.url}
            src={state.url}
            title={state.title}
            data-testid="game-launch-iframe"
            className={`absolute inset-0 w-full h-full bg-black transition-opacity duration-500 ${showGame ? "opacity-100 z-[110]" : "opacity-0 z-[10] pointer-events-none"}`}
            allow="autoplay; payment; gyroscope; accelerometer; clipboard-write"
            sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-modals allow-presentation allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
            referrerPolicy="strict-origin-when-cross-origin"
            onLoad={() => { setIframeLoaded(true); setIframeFailed(false); }}
          />
        )}

        {/* Iframe failed to load — show fallback overlay above iframe */}
        {showFailure && (
          <IframeFailurePanel state={state} theme={theme} onClose={onClose} />
        )}

        {/* Demo landing when provider not configured */}
        {state.phase === "ready" && state.demo && showGame && <DemoLandingPanel state={state} />}
      </div>
    </div>
  );
}

/* ---------------- Iframe failure / domain-blocked fallback ---------------- */

function IframeFailurePanel({ state, theme, onClose }) {
  return (
    <div
      className="absolute inset-0 z-[115] flex items-center justify-center p-6"
      style={{
        background:
          `radial-gradient(ellipse at 50% 30%, ${theme.bgInner} 0%, ${theme.bgMid} 50%, ${theme.bgOuter} 100%)`,
      }}
      data-testid="game-launch-failure"
    >
      <div className="max-w-lg w-full text-center space-y-5">
        <div
          className="mx-auto h-16 w-16 rounded-full flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${theme.accent2}, ${theme.accent})`,
            boxShadow: `0 8px 28px ${theme.accent}55`,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div>
          <h2 className="text-white font-display text-2xl sm:text-3xl font-bold leading-tight">
            {state.popup_blocked
              ? "Your browser blocked the new tab"
              : `${state.title} couldn't open here`}
          </h2>
          <p className="mt-2 text-white/65 text-sm leading-relaxed">
            {state.popup_blocked ? (
              <>
                Your browser&apos;s popup blocker stopped this game from
                opening. Tap the button below to open it in a new tab — or
                allow popups for this site in your browser settings so games
                launch automatically next time.
              </>
            ) : (
              <>
                This game isn&apos;t available to play inside this page. You
                can open it in a new tab using the button below, or pick a
                different game from the lobby.
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5">
          <a
            href={state.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="game-launch-failure-newtab"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-[13px] font-semibold text-white transition-transform duration-150 hover:scale-[1.03] active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${theme.accent2}, ${theme.accent})`,
              boxShadow: `0 6px 20px ${theme.accent}55`,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 3h7v7" />
              <path d="M10 14L21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
            Open in new tab
          </a>
          <button
            type="button"
            onClick={onClose}
            data-testid="game-launch-failure-close"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md text-[13px] font-semibold text-white/85 hover:text-white transition-colors"
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            Back to lobby
          </button>
        </div>
        <p className="text-[11px] text-white/35 tracking-wider uppercase">
          Maxlive88 · Secure session
        </p>
      </div>
    </div>
  );
}

/* ---------------- Branded loading skin ---------------- */

const LOADING_TIPS = [
  "Play Smart, Win Big — every spin counts",
  "All your wins · paid within minutes",
  "Licensed by the Government of Anjouan",
  "256-bit SSL · Bank-grade encryption",
  "100% Welcome Bonus on first deposit",
  "24/7 Live Support · Bangla & English",
];

function GameLaunchHeader({ title, username, clientIp, onClose, onToggleFullscreen, isFullscreen, height = 56 }) {
  const safeName = (username || "Player").trim();
  const safeIp = (clientIp || "").trim();
  const safeTitle = (title || "Loading").trim();
  return (
    <div
      className="relative flex items-center justify-between px-3 sm:px-4 py-2 border-b border-neutral-200 bg-white shrink-0 z-[120]"
      style={{ height }}
      data-testid="game-launch-header"
    >
      {/* Left — player username + IP stacked */}
      <div className="flex flex-col leading-tight min-w-0 max-w-[34%]">
        <span
          data-testid="game-launch-loader-username"
          className="text-[14px] sm:text-[15px] font-extrabold tracking-tight text-neutral-900 truncate"
          title={safeName}
        >
          {safeName}
        </span>
        <span
          data-testid="game-launch-loader-ip"
          className="text-[12px] sm:text-[13px] font-medium text-neutral-700 truncate font-mono"
          title={safeIp}
        >
          {safeIp || "\u00A0"}
        </span>
      </div>

      {/* Center — game name in plain bold text */}
      <div className="absolute left-1/2 -translate-x-1/2 px-3 max-w-[52%] text-center pointer-events-none">
        <div
          data-testid="game-launch-loader-title"
          className="text-[15px] sm:text-[17px] font-bold text-neutral-900 truncate"
          style={{ lineHeight: 1.1 }}
          title={safeTitle}
        >
          {safeTitle}
        </div>
      </div>

      {/* Right — Fullscreen toggle + Close */}
      <div className="ml-auto flex items-center gap-1">
        {onToggleFullscreen && (
          <button
            type="button"
            onClick={onToggleFullscreen}
            onTouchEnd={(e) => { e.preventDefault(); onToggleFullscreen(); }}
            data-testid="game-launch-loader-fullscreen"
            aria-label={isFullscreen ? "exit fullscreen" : "enter fullscreen"}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100 active:scale-95 transition-all"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          >
            {isFullscreen ? (
              /* compress icon */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 14h6v6" />
                <path d="M20 10h-6V4" />
                <path d="M14 10l7-7" />
                <path d="M3 21l7-7" />
              </svg>
            ) : (
              /* expand icon */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
            )}
          </button>
        )}

        <button
          type="button"
          onClick={onClose}
          onTouchEnd={(e) => { e.preventDefault(); onClose && onClose(); }}
          data-testid="game-launch-loader-close"
          aria-label="close"
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-neutral-700 hover:bg-neutral-100 active:scale-95 transition-all"
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ---------------- Branded loading skin (body only — header is persistent) ---------------- */

function GameLoadingSkin({ providerCode, title, theme }) {
  // 9-Wickets (WCKT) gets a custom cricket-themed loader. Every other
  // provider gets the polished generic Maxlive88 spinner. The WCKT loader
  // uses the same provider theme palette (emerald + gold) as the rest of
  // the brand and looks identical on phones and desktops thanks to the
  // viewport-relative sizing (`clamp(...)` units).
  const isWckt =
    String(providerCode || "").toUpperCase() === "WCKT" ||
    /9.?wicket/i.test(String(title || ""));

  if (isWckt) {
    return <NineWicketsLoadingSkin title={title} theme={theme} />;
  }

  return (
    <div
      className="absolute inset-0 z-[105] flex items-center justify-center bg-white select-none gl-fade-in"
      data-testid="game-launch-loader"
      aria-label="loading"
    >
      <div className="flex items-center gap-2.5" data-testid="game-launch-loader-dots">
        <span className="gl-dot-bounce" style={{ animationDelay: "0s" }} />
        <span className="gl-dot-bounce" style={{ animationDelay: "0.16s" }} />
        <span className="gl-dot-bounce" style={{ animationDelay: "0.32s" }} />
      </div>

      <style>{`
        @keyframes gl-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .gl-fade-in { animation: gl-fade-in 0.18s ease-out both; }

        @keyframes gl-dot-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
          40% { transform: translateY(-10px); opacity: 1; }
        }
        .gl-dot-bounce {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 9999px;
          background: #9ca3af;
          animation: gl-dot-bounce 1.05s ease-in-out infinite both;
        }
      `}</style>
    </div>
  );
}

/* ---------------- 9 Wickets cricket-themed loading skin ---------------- */
/**
 * Premium cricket-themed loader shown ONLY for the 9 Wickets (WCKT) provider.
 * Designed to feel like a real sportsbook splash screen — emerald pitch
 * gradient, animated ball bouncing across stumps, brand wordmark + tagline,
 * and an indeterminate progress sliver underneath. Looks identical and
 * proportional on both phones (clamp font sizes + svh layout) and large
 * desktop monitors (capped max-width on the inner card).
 */
function NineWicketsLoadingSkin({ title, theme }) {
  const t = theme || {
    accent: "#19a55c", accent2: "#2bd07c", gold: "#ffc933",
    bgInner: "#0e3a25", bgMid: "#062014", bgOuter: "#02060a",
  };
  return (
    <div
      className="absolute inset-0 z-[105] flex items-center justify-center select-none nw-fade-in overflow-hidden"
      data-testid="game-launch-loader"
      aria-label="9 Wickets loading"
      style={{
        background: `radial-gradient(ellipse at 50% 32%, ${t.bgInner} 0%, ${t.bgMid} 55%, ${t.bgOuter} 100%)`,
      }}
    >
      {/* Stadium light rays (soft, diagonal) — purely decorative */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none nw-rays"
        style={{
          background: `
            radial-gradient(circle at 20% 0%, ${t.gold}25 0%, transparent 32%),
            radial-gradient(circle at 80% 0%, ${t.accent2}30 0%, transparent 32%),
            radial-gradient(circle at 50% 100%, ${t.accent}1f 0%, transparent 45%)
          `,
        }}
      />
      {/* Faint pitch grid lines */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 100%",
          maskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 30%, transparent 80%)",
        }}
      />

      <div className="relative w-full max-w-[560px] px-6 sm:px-8 text-center">
        {/* Brand wordmark — large, bold, gold gradient on emerald */}
        <div
          className="relative inline-flex items-baseline gap-2 sm:gap-3 nw-rise"
          style={{ animationDelay: "0.05s" }}
        >
          <span
            className="font-display font-black tracking-tight"
            style={{
              fontSize: "clamp(34px, 7vw, 56px)",
              lineHeight: 1,
              background: `linear-gradient(180deg, ${t.gold} 0%, #fff5d4 55%, ${t.gold} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              filter: `drop-shadow(0 4px 18px ${t.gold}55)`,
            }}
          >
            9
          </span>
          <span
            className="font-display font-extrabold tracking-tight"
            style={{
              fontSize: "clamp(22px, 4.6vw, 36px)",
              lineHeight: 1,
              color: "#ffffff",
              letterSpacing: "0.02em",
            }}
          >
            Wickets
          </span>
        </div>

        <div
          className="mt-1.5 sm:mt-2 text-[11px] sm:text-[12px] font-medium tracking-[0.22em] uppercase nw-rise"
          style={{ color: `${t.accent2}`, animationDelay: "0.18s" }}
        >
          Live Cricket · Exchange · Sportsbook
        </div>

        {/* Stumps + bouncing ball animation */}
        <div
          className="relative mx-auto mt-7 sm:mt-9 h-[80px] sm:h-[96px] w-full max-w-[320px] nw-rise"
          style={{ animationDelay: "0.32s" }}
          aria-hidden="true"
        >
          {/* Ground line */}
          <div
            className="absolute left-0 right-0 bottom-[14px] h-[2px] rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent 0%, ${t.accent}88 18%, ${t.accent2} 50%, ${t.accent}88 82%, transparent 100%)`,
              boxShadow: `0 0 14px ${t.accent2}66`,
            }}
          />
          {/* Stumps (3 vertical pillars + 2 bails) */}
          <div className="absolute right-2 sm:right-4 bottom-[16px] flex items-end gap-[5px] sm:gap-[6px]">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="block rounded-t-sm rounded-b-[1px]"
                style={{
                  width: "5px",
                  height: "44px",
                  background: `linear-gradient(180deg, #fff 0%, ${t.gold} 100%)`,
                  boxShadow: `0 0 6px ${t.gold}aa`,
                }}
              />
            ))}
            {/* Bails (horizontal pieces on top) */}
            <span
              className="absolute -top-[3px] left-0 right-0 h-[2px] rounded-full"
              style={{ background: `linear-gradient(90deg, #fff, ${t.gold})` }}
            />
          </div>

          {/* Cricket ball — bouncing across the pitch toward the stumps */}
          <span
            className="absolute bottom-[16px] h-3.5 w-3.5 sm:h-4 sm:w-4 rounded-full nw-ball"
            style={{
              background: `radial-gradient(circle at 30% 30%, #ff4d4d 0%, #b91c1c 65%, #5a0a0a 100%)`,
              boxShadow: `0 0 10px rgba(255, 60, 60, 0.55), inset -2px -2px 3px rgba(0,0,0,0.5)`,
            }}
          />
          {/* Ball seam */}
          <span
            aria-hidden="true"
            className="absolute bottom-[18px] left-[14%] h-[1px] w-2 sm:w-2.5 rounded-full nw-ball-seam"
            style={{ background: "rgba(255,255,255,0.55)" }}
          />
        </div>

        {/* Game title (small, white-on-emerald) */}
        {!!title && (
          <div
            className="mt-5 sm:mt-7 text-[12px] sm:text-[13px] font-semibold tracking-wide truncate nw-rise"
            style={{ color: "rgba(255,255,255,0.78)", animationDelay: "0.48s" }}
            data-testid="game-launch-loader-title-wckt"
          >
            Opening {title}
          </div>
        )}

        {/* Indeterminate progress bar */}
        <div
          className="relative mx-auto mt-3 sm:mt-4 h-[3px] w-full max-w-[260px] rounded-full overflow-hidden nw-rise"
          style={{
            background: "rgba(255,255,255,0.10)",
            animationDelay: "0.6s",
          }}
          data-testid="game-launch-loader-progress"
        >
          <span
            className="absolute inset-y-0 left-0 w-1/3 rounded-full nw-progress-sweep"
            style={{
              background: `linear-gradient(90deg, transparent, ${t.gold}, ${t.accent2}, transparent)`,
              boxShadow: `0 0 10px ${t.gold}aa`,
            }}
          />
        </div>

        {/* Trust strip */}
        <div
          className="mt-5 sm:mt-7 flex items-center justify-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] tracking-wider uppercase nw-rise"
          style={{ color: "rgba(255,255,255,0.45)", animationDelay: "0.74s" }}
        >
          <span className="inline-flex items-center gap-1.5">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
            </svg>
            Secure
          </span>
          <span className="opacity-40">·</span>
          <span>Licensed</span>
          <span className="opacity-40">·</span>
          <span>Fair Play</span>
        </div>
      </div>

      <style>{`
        @keyframes nw-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .nw-fade-in { animation: nw-fade-in 0.22s ease-out both; }

        @keyframes nw-rise {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .nw-rise {
          animation: nw-rise 0.52s cubic-bezier(0.21, 0.96, 0.36, 1) both;
        }

        @keyframes nw-rays {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.92; }
        }
        .nw-rays { animation: nw-rays 4.2s ease-in-out infinite; }

        @keyframes nw-ball-bounce {
          0%   { left: 6%;  bottom: 16px; transform: rotate(0deg); }
          20%  { left: 26%; bottom: 50px; transform: rotate(180deg); }
          40%  { left: 46%; bottom: 16px; transform: rotate(360deg); }
          55%  { left: 60%; bottom: 38px; transform: rotate(520deg); }
          70%  { left: 72%; bottom: 16px; transform: rotate(680deg); }
          85%  { left: 80%; bottom: 28px; transform: rotate(820deg); }
          100% { left: 86%; bottom: 16px; transform: rotate(960deg); }
        }
        .nw-ball {
          animation: nw-ball-bounce 2.4s cubic-bezier(0.36, 0, 0.66, -0.56) infinite;
        }
        @keyframes nw-ball-seam-move {
          0%   { left: 6%; }
          100% { left: 86%; }
        }
        .nw-ball-seam {
          animation: nw-ball-seam-move 2.4s linear infinite;
          opacity: 0.0; /* purely decorative; main ball element is enough */
        }

        @keyframes nw-progress-sweep {
          0%   { transform: translateX(-110%); }
          100% { transform: translateX(310%); }
        }
        .nw-progress-sweep {
          animation: nw-progress-sweep 1.6s cubic-bezier(0.6, 0, 0.4, 1) infinite;
        }

        /* Reduced-motion respect — disable bounce / rays / progress sweep */
        @media (prefers-reduced-motion: reduce) {
          .nw-rise, .nw-ball, .nw-rays, .nw-progress-sweep {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </div>
  );
}

function DemoLandingPanel({ state }) {
  const reason = state?.demo_reason || "";
  const isProviderDeclined = reason === "provider_declined";
  const isNetworkError = reason === "provider_network_error";
  // Show the Bangla "Balance Refresh প্রয়োজন" prompt ONLY for 9 Wickets (WCKT)
  // — every other game keeps the original English "try a different one" wording.
  const isWcktGame =
    /wicket/i.test(state?.provider || "") ||
    /wckt/i.test(state?.provider || "") ||
    /wckt/i.test(state?.provider_code || "");

  return (
    <div className="absolute inset-0 flex items-center justify-center p-6 text-center z-[110] bg-black overflow-auto">
      <div className="max-w-xl w-full space-y-4">
        <h2 className="text-white font-display text-2xl font-bold">{state.title}</h2>

        {isProviderDeclined ? (
          <div className="space-y-3">
            <div
              className="rounded-lg px-4 py-3 text-left text-[13px] text-amber-200/90"
              style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)" }}
            >
              {isWcktGame ? (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-amber-300/80 mb-1">
                    Balance Refresh প্রয়োজন
                  </div>
                  <div className="font-medium text-amber-100 leading-relaxed">
                    ⚠️ 9 Wicket-এ প্রবেশ করার জন্য <span className="font-bold underline">প্রতিবার</span> গেমে ঢোকার আগে <span className="font-bold">Balance Refresh</span> করা <span className="font-bold">অবশ্যই</span> বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-amber-300/80 mb-1">
                    Game temporarily unavailable
                  </div>
                  <div className="font-medium text-amber-100">
                    This game can&apos;t be opened right now. Please try a different
                    game — most other titles in the lobby are available immediately.
                  </div>
                </>
              )}
            </div>
          </div>
        ) : isNetworkError ? (
          <div className="space-y-3">
            <div
              className="rounded-lg px-4 py-3 text-left text-[13px] text-red-200/90"
              style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)" }}
            >
              {isWcktGame ? (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-red-300/80 mb-1">
                    Balance Refresh প্রয়োজন
                  </div>
                  <div className="font-medium text-red-100 leading-relaxed">
                    ⚠️ 9 Wicket-এ প্রবেশ করার জন্য <span className="font-bold underline">প্রতিবার</span> গেমে ঢোকার আগে <span className="font-bold">Balance Refresh</span> করা <span className="font-bold">অবশ্যই</span> বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-red-300/80 mb-1">
                    Service temporarily unavailable
                  </div>
                  <div className="font-medium text-red-100">
                    We couldn&apos;t connect to this game just now. Please try a
                    different game — we&apos;ll have this one back shortly.
                  </div>
                </>
              )}
            </div>
          </div>
        ) : isWcktGame ? (
          <p className="text-white/80 text-sm leading-relaxed">
            <span className="block text-amber-300 font-semibold mb-1">⚠️ Balance Refresh প্রয়োজন</span>
            9 Wicket-এ প্রবেশ করার জন্য <span className="font-bold underline">প্রতিবার</span> গেমে ঢোকার আগে <span className="font-bold">Balance Refresh</span> করা <span className="font-bold">অবশ্যই</span> বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।
          </p>
        ) : (
          <p className="text-white/55 text-sm leading-relaxed">
            This game isn&apos;t available right now. Please browse the lobby
            and pick another title — most games are ready to play immediately.
          </p>
        )}
      </div>
    </div>
  );
}
